import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MarkdownMessage } from "./MarkdownMessage";
import { useAssistantChat, type ChatMessage, type ToolCall } from "./useAssistantChat";
import { useAssistantAccess } from "./useAssistantAccess";
import { SUGGESTED_PROMPTS, type AssistantScreen } from "./suggestedPrompts";
import { toolCallLink } from "./assistantToolCallLink";
import { ASK_ASSISTANT_EVENT, type AskAssistantDetail } from "./askAssistant";
import type { ChatContext } from "./assistantStream";
import { splitFollowUps } from "./followUps";

interface Props {
  screen: AssistantScreen;
  /** JSON snapshot of the screen's current filter/URL state, forwarded as Claude's lens. */
  activeFilters?: unknown;
  /**
   * What the answers are scoped to, in plain words ("Whole org", "Erica Heaslip's team"), shown above the chat
   * and sent with each question; plus the open drill, the record on a detail screen and when the data on screen
   * was loaded (2026-10-29). All optional: a screen without them sends what it sent before.
   */
  context?: Pick<ChatContext, "scope" | "drill" | "recordId" | "dataAsOfUtc">;
  defaultWidth?: number;
  /** When true, the collapsed control floats (fixed) instead of docking in-flow — for screens
   *  without a filter rail to stack against (e.g. request detail). */
  floatingCollapsed?: boolean;
}

const COLLAPSED_KEY = "ob-chat-collapsed";
const WIDTH_KEY = "ob-chat-width";

/** Keep the drawer between 360px and 70% of the viewport. */
function clampWidth(n: number): number {
  const max = typeof window !== "undefined" ? window.innerWidth * 0.7 : 760;
  return Math.max(360, Math.min(max, n));
}

/**
 * Docked right-edge "Ask Claude" panel shared by every analytics screen. Mirrors the
 * filter rail's collapse / drag-resize / localStorage-persist mechanics. Streams answers token by
 * token via {@link useAssistantChat}; renders assistant text as markdown with inline tool-call
 * indicators. `Cmd/Ctrl+J` toggles it open/closed.
 */
export function AssistantPanel({ screen, activeFilters, context, defaultWidth = 480, floatingCollapsed = false }: Props) {
  const { messages, status, usage, activeTool, send, clear } = useAssistantChat();
  const navigate = useNavigate();
  const access = useAssistantAccess();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    // Default to collapsed on first use so the panel doesn't steal space unprompted.
    return window.localStorage.getItem(COLLAPSED_KEY) !== "0";
  });
  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return defaultWidth;
    const stored = Number(window.localStorage.getItem(WIDTH_KEY));
    const initial =
      Number.isFinite(stored) && stored > 0
        ? stored
        : Math.min(760, Math.max(420, Math.round(window.innerWidth * 0.5)));
    return clampWidth(initial);
  });
  // The drawer is fixed-position; sit it just below the topbar (measured, not hardcoded).
  const [topOffset, setTopOffset] = useState<number>(56);
  const [draft, setDraft] = useState("");
  const panelRef = useRef<HTMLElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const measure = () => {
      const bar = document.querySelector(".topbar");
      setTopOffset(bar ? Math.round(bar.getBoundingClientRect().height) : 56);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem(WIDTH_KEY, String(width));
  }, [width]);

  // Cmd/Ctrl+J toggles the panel from anywhere on the screen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "j" || e.key === "J")) {
        e.preventDefault();
        setCollapsed((c) => !c);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Another part of the screen asked a question on the user's behalf (e.g. the attention banner's
  // "Ask Claude why"): open the panel and send it with this screen's context.
  const sendRef = useRef(send);
  const chatContext = (): ChatContext => ({ currentScreen: screen, activeFilters, ...context });
  const contextRef = useRef(chatContext);
  sendRef.current = send;
  contextRef.current = chatContext;
  useEffect(() => {
    const onAsk = (e: Event) => {
      const prompt = (e as CustomEvent<AskAssistantDetail>).detail?.prompt?.trim();
      if (!prompt) return;
      setCollapsed(false);
      void sendRef.current(prompt, contextRef.current());
    };
    window.addEventListener(ASK_ASSISTANT_EVENT, onAsk);
    return () => window.removeEventListener(ASK_ASSISTANT_EVENT, onAsk);
  }, []);

  // Keep the newest message in view as tokens stream in.
  useEffect(() => {
    const el = historyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activeTool]);

  const onResizeDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    const right = rect.right;
    const onMove = (ev: MouseEvent) => {
      setWidth(clampWidth(right - ev.clientX));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const submit = () => {
    const text = draft.trim();
    if (!text || status === "streaming") return;
    setDraft("");
    void send(text, chatContext());
  };

  const onPrompt = (prompt: string) => {
    if (status === "streaming") return;
    void send(prompt, chatContext());
  };

  // Pilot allowlist: hide the panel entirely unless the user is allowed (enforced server-side too).
  if (access.loading || !access.allowed) {
    return null;
  }

  if (collapsed) {
    return (
      <aside
        className={`chat-panel-collapsed${floatingCollapsed ? " chat-tab" : ""}`}
        onClick={() => setCollapsed(false)}
        title="Ask Claude (⌘/Ctrl+J)"
      >
        <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
          smart_toy
        </span>
        <span className="vlabel">Ask Claude</span>
      </aside>
    );
  }

  const isEmpty = messages.length === 0;

  return (
    <aside
      className="chat-panel chat-drawer"
      ref={panelRef}
      style={{ width, top: topOffset }}
      aria-label="Ask Claude assistant"
    >
      <div className="resize-handle" onMouseDown={onResizeDown} title="Drag to resize" />

      <div className="chat-panel-head">
        <h4>
          <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
            smart_toy
          </span>
          Ask Claude
        </h4>
        <div className="chat-panel-head-actions">
          {!isEmpty && (
            <button
              type="button"
              className="chat-head-btn chat-clear-btn"
              onClick={clear}
              title="Clear chat"
              aria-label="Clear chat"
            >
              <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
                delete_sweep
              </span>
              Clear chat
            </button>
          )}
          <button
            type="button"
            className="chat-head-btn"
            onClick={() => setCollapsed(true)}
            title="Collapse"
            aria-label="Collapse assistant"
          >
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
              right_panel_close
            </span>
          </button>
        </div>
      </div>

      {context?.scope && (
        <div className="chat-scope" role="status" aria-live="polite">
          Answering for: <b>{context.scope}</b>
        </div>
      )}

      <div className="chat-messages" ref={historyRef}>
        {isEmpty ? (
          <div className="chat-empty">
            <p className="chat-empty-lead">Ask a question about the analytics data.</p>
            <div className="chat-suggested">
              {SUGGESTED_PROMPTS[screen].map((p) => (
                <button key={p} type="button" className="chat-suggested-item" onClick={() => onPrompt(p)}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <MessageBubble
              key={i}
              message={m}
              onCitation={(path) => navigate(path)}
              onNavigate={navigate}
              // Follow-ups only under the latest answer, once it has finished.
              onFollowUp={i === messages.length - 1 && status !== "streaming" ? onPrompt : undefined}
            />
          ))
        )}

        {status === "streaming" && (
          <div className="chat-status" role="status">
            <span className="chat-dot" aria-hidden="true" />
            {activeTool ? `Calling ${activeTool}…` : "Claude is thinking…"}
          </div>
        )}
      </div>

      <div className="chat-input">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Ask a question…  (Enter to send, Shift+Enter for newline)"
          rows={2}
          aria-label="Message"
        />
        <button
          type="button"
          className="chat-send"
          onClick={submit}
          disabled={status === "streaming" || draft.trim().length === 0}
          aria-label="Send"
          title="Send"
        >
          <span className="material-symbols-rounded" style={{ fontSize: 20 }}>
            send
          </span>
        </button>
      </div>

      <div className="chat-foot">
        <span>Claude can make mistakes. Verify important findings before acting.</span>
        {usage && (
          <span className="chat-usage" title="Tokens used this session">
            {usage.inputTokens + usage.outputTokens} tok
          </span>
        )}
      </div>
    </aside>
  );
}

function MessageBubble({
  message,
  onCitation,
  onNavigate,
  onFollowUp,
}: {
  message: ChatMessage;
  onCitation: (path: string) => void;
  onNavigate: (path: string) => void;
  onFollowUp?: (question: string) => void;
}) {
  const isUser = message.role === "user";
  const { body, followUps } = isUser ? { body: message.text, followUps: [] } : splitFollowUps(message.text);
  const link = isUser ? null : toolCallLink(message.toolCalls);
  return (
    <div className={`chat-msg ${isUser ? "chat-msg-user" : "chat-msg-assistant"}`}>
      {message.toolCalls.length > 0 && <ToolCallSummary calls={message.toolCalls} />}
      {isUser ? (
        <p className="chat-msg-text">{message.text}</p>
      ) : body ? (
        <MarkdownMessage text={body} onCitation={onCitation} />
      ) : null}
      {link && (
        <button type="button" className="chat-grid-link" onClick={() => onNavigate(link.url)}>
          {link.label}
          <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
            arrow_forward
          </span>
        </button>
      )}
      {onFollowUp && followUps.length > 0 && (
        <div className="chat-followups" aria-label="Follow-up questions">
          {followUps.map((q) => (
            <button key={q} type="button" className="chat-followup" onClick={() => onFollowUp(q)}>
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A single, static line indicating the assistant consulted the data (e.g. "Looked up data · 3
 * steps") — enough to convey it used real tools, without exposing tool names or raw input JSON.
 */
function ToolCallSummary({ calls }: { calls: ToolCall[] }) {
  const label = calls.length === 1 ? "Looked up data" : `Looked up data · ${calls.length} steps`;
  return (
    <div className="chat-toolcall">
      <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
        database
      </span>
      {label}
    </div>
  );
}
